package fr.manyverse.app.presentation.dice3d

import kotlin.math.abs
import kotlin.math.acos
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.random.Random

/** Quaternion unitaire représentant une rotation 3D. */
data class Quaternion(val w: Float, val x: Float, val y: Float, val z: Float) {

    operator fun times(other: Quaternion) = Quaternion(
        w * other.w - x * other.x - y * other.y - z * other.z,
        w * other.x + x * other.w + y * other.z - z * other.y,
        w * other.y - x * other.z + y * other.w + z * other.x,
        w * other.z + x * other.y - y * other.x + z * other.w
    )

    fun normalized(): Quaternion {
        val norm = sqrt(w * w + x * x + y * y + z * z)
        return if (norm < 1e-9f) IDENTITY else Quaternion(w / norm, x / norm, y / norm, z / norm)
    }

    /** Applique la rotation au vecteur : q · v · q⁻¹ (forme optimisée sans quaternion pur). */
    fun rotate(v: Vec3): Vec3 {
        val qv = Vec3(x, y, z)
        val t = (qv cross v) * 2f
        return v + t * w + (qv cross t)
    }

    /**
     * Rotation inverse : q⁻¹ · v · q. Sert à ramener un vecteur du repère du plateau vers
     * celui du dé, là où son tenseur d'inertie est diagonal.
     */
    fun inverseRotate(v: Vec3): Vec3 {
        val qv = Vec3(-x, -y, -z)
        val t = (qv cross v) * 2f
        return v + t * w + (qv cross t)
    }

    /**
     * Composante z de [rotate], sans allouer de [Vec3]. La fonction d'appui du dé balaie
     * tous ses sommets à chaque pas de simulation, et seule leur hauteur l'intéresse.
     */
    fun rotatedZ(v: Vec3): Float {
        val tx = 2f * (y * v.z - z * v.y)
        val ty = 2f * (z * v.x - x * v.z)
        val tz = 2f * (x * v.y - y * v.x)
        return v.z + w * tz + (x * ty - y * tx)
    }

    /**
     * Même rotation que [rotate], déposée dans [out] à partir de [offset]. Variante sans
     * allocation, pour les boucles de rendu qui tournent des centaines de sommets par image.
     */
    fun rotateInto(v: Vec3, out: FloatArray, offset: Int) {
        val tx = 2f * (y * v.z - z * v.y)
        val ty = 2f * (z * v.x - x * v.z)
        val tz = 2f * (x * v.y - y * v.x)
        out[offset] = v.x + w * tx + (y * tz - z * ty)
        out[offset + 1] = v.y + w * ty + (z * tx - x * tz)
        out[offset + 2] = v.z + w * tz + (x * ty - y * tx)
    }

    companion object {
        val IDENTITY = Quaternion(1f, 0f, 0f, 0f)

        fun fromAxisAngle(axis: Vec3, angleRadians: Float): Quaternion {
            val unit = axis.normalized()
            val half = angleRadians / 2f
            val s = sin(half)
            return Quaternion(cos(half), unit.x * s, unit.y * s, unit.z * s)
        }

        /** Rotation minimale amenant [from] sur [to] (vecteurs unitaires attendus). */
        fun rotationBetween(from: Vec3, to: Vec3): Quaternion {
            val dot = (from dot to).coerceIn(-1f, 1f)
            if (dot > 1f - 1e-6f) return IDENTITY
            if (dot < -1f + 1e-6f) {
                // Vecteurs opposés : demi-tour autour de n'importe quel axe perpendiculaire.
                var axis = from cross Vec3(1f, 0f, 0f)
                if (axis.length < 1e-6f) axis = from cross Vec3(0f, 1f, 0f)
                return fromAxisAngle(axis, kotlin.math.PI.toFloat())
            }
            val axis = from cross to
            return fromAxisAngle(axis, acos(dot))
        }

        /** Interpolation sphérique par l'arc le plus court. */
        fun slerp(a: Quaternion, b: Quaternion, t: Float): Quaternion {
            var dot = a.w * b.w + a.x * b.x + a.y * b.y + a.z * b.z
            var end = b
            if (dot < 0f) {
                dot = -dot
                end = Quaternion(-b.w, -b.x, -b.y, -b.z)
            }
            if (dot > 1f - 1e-5f) {
                // Quaternions quasi identiques : interpolation linéaire suffisante.
                return Quaternion(
                    a.w + (end.w - a.w) * t,
                    a.x + (end.x - a.x) * t,
                    a.y + (end.y - a.y) * t,
                    a.z + (end.z - a.z) * t
                ).normalized()
            }
            val theta = acos(dot.coerceIn(-1f, 1f))
            val sinTheta = sin(theta)
            val wa = sin((1f - t) * theta) / sinTheta
            val wb = sin(t * theta) / sinTheta
            return Quaternion(
                a.w * wa + end.w * wb,
                a.x * wa + end.x * wb,
                a.y * wa + end.y * wb,
                a.z * wa + end.z * wb
            ).normalized()
        }

        /** Rotation uniformément aléatoire (méthode de Shoemake). */
        fun random(random: Random): Quaternion {
            val u1 = random.nextFloat()
            val u2 = random.nextFloat() * 2f * kotlin.math.PI.toFloat()
            val u3 = random.nextFloat() * 2f * kotlin.math.PI.toFloat()
            val a = sqrt(1f - u1)
            val b = sqrt(u1)
            return Quaternion(a * sin(u2), a * cos(u2), b * sin(u3), b * cos(u3)).normalized()
        }
    }
}

internal fun approxEquals(a: Vec3, b: Vec3, epsilon: Float = 1e-3f): Boolean =
    abs(a.x - b.x) < epsilon && abs(a.y - b.y) < epsilon && abs(a.z - b.z) < epsilon
